import { useRef } from 'react'
import { useAppSelector } from '@services/store'
import {
	REQUEST_CONFIGS_KEYS,
	REQUEST_TN_DETAILS_STEPS_NAME
} from '@services/constants'
import requestForm from '../../request.form'
import {
	isGR7_1RenovationOrDevelopment,
	isRenovationSectionB,
	isRenovationSectionA
} from '../__index.utils'

export const STEP4_1FIELDS = {
	typesOfWorkRenovation: 'typesOfWorkRenovation',
	workDescriptionRenovationInterior: 'workDescriptionRenovationInterior',
	areaOfPremisesRenovationInterior: 'areaOfPremisesRenovationInterior',
	// areaOfPremisesUnitRenovationInterior: 'areaOfPremisesUnitRenovationInterior',
	currentNumberOfUnitsRenovationInterior:
		'currentNumberOfUnitsRenovationInterior',
	numberOfUnitsAddedRenovationInterior: 'numberOfUnitsAddedRenovationInterior',
	workDescriptionRenovationExterior: 'workDescriptionRenovationExterior',
	existingMaterialsRenovationExterior: 'existingMaterialsRenovationExterior',
	projectedMaterialsRenovationExterior: 'projectedMaterialsRenovationExterior',
	isTerraceAdditionOrModificationRenovation:
		'isTerraceAdditionOrModificationRenovation',
	isParkingAreaModificationRenovation: 'isParkingAreaModificationRenovation',
	isFloorAreaModificationRenovation: 'isFloorAreaModificationRenovation'
} as const

export type Step4_1Fields = keyof typeof STEP4_1FIELDS

/**
 * useStep4_1State - Custom hook to manage the state for step 4_1 of the GR7 commercial Permit form..
 * It retrieves the necessary answers from the Redux store and prepares the state for the form inputs.
 *
 * @returns {Object} An object containing the state for each input in step 4_1.
 */

// --- 4.1. State HOOK for Step 4.1 ---
// This hook is used to manage the state of Step 4.1 in the GR6
export function useStep4_1State() {
	const answers = useAppSelector((state) => state.request.configs)
	const { pageAssets } = requestForm()

	const isGR7_1 = isGR7_1RenovationOrDevelopment(answers?.whatToDo)

	return {
		// Quel(s) type(s) de travaux souhaitez-vous effectuer?
		typesOfWorkRenovation: {
			name: 'typesOfWorkRenovation',
			label:
				pageAssets?.request_form_residentialRenovationOrRepairPermit_typesOfWork,
			labelKey: 'request_form_residentialRenovationOrRepairPermit_typesOfWork',
			value: answers?.typesOfWorkRenovation?.toString() || '',
			required: isGR7_1,
			ref: useRef<HTMLSelectElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},
		// Description des travaux intérieurs
		workDescriptionRenovationInterior: {
			name: 'workDescriptionRenovationInterior',
			label: pageAssets?.request_form_commercialPermit_workDescriptionInterior,
			labelKey: 'request_form_commercialPermit_workDescriptionInterior',
			value: answers?.workDescriptionRenovationInterior?.toString() || '',
			required: isGR7_1 && isRenovationSectionA(answers?.typesOfWorkRenovation),
			ref: useRef<HTMLTextAreaElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},
		// Superficie du ou des locaux
		areaOfPremisesRenovationInterior: {
			name: 'areaOfPremisesRenovationInterior',
			label: pageAssets?.request_form_commercialPermit_areaOfPremises,
			labelKey: 'request_form_commercialPermit_areaOfPremises',
			value: answers?.areaOfPremisesRenovationInterior?.toString() || '',
			required: false,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},
		areaOfPremisesUnitRenovationInterior: {
			name: 'areaOfPremisesUnitRenovationInterior',
			label: pageAssets?.request_form_commercialPermit_areaOfPremisesUnit,
			labelKey: 'request_form_commercialPermit_areaOfPremisesUnit',
			value:
				answers?.areaOfPremisesUnitRenovationInterior?.toString() ||
				REQUEST_CONFIGS_KEYS.measurement_unit.meters_unit,
			required: false,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},

		// Nombre de logements actuels
		currentNumberOfUnitsRenovationInterior: {
			name: 'currentNumberOfUnitsRenovationInterior',
			label:
				pageAssets?.request_form_commercialPermit_currentNumberOfUnitsRenovationInterior,
			labelKey:
				'request_form_commercialPermit_currentNumberOfUnitsRenovationInterior',
			value: answers?.currentNumberOfUnitsRenovationInterior?.toString() || '',
			required: false,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},

		// Nombre de logements ajoutés (inscrire 0, si non applicable)
		numberOfUnitsAddedRenovationInterior: {
			name: 'numberOfUnitsAddedRenovationInterior',
			label:
				pageAssets?.request_form_commercialPermit_numberOfUnitsAddedRenovationInterior,
			labelKey:
				'request_form_commercialPermit_numberOfUnitsAddedRenovationInterior',
			value: answers?.numberOfUnitsAddedRenovationInterior?.toString() || '',
			required: false,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},
		// ************************************************
		// Section B - Rénovation et aménagement extérieur

		// ************************************************
		// Description des travaux extérieurs
		workDescriptionRenovationExterior: {
			name: 'workDescriptionRenovationExterior',
			label: pageAssets?.request_form_commercialPermit_workDescriptionExterior,
			labelKey: 'request_form_commercialPermit_workDescriptionExterior',
			value: answers?.workDescriptionRenovationExterior?.toString() || '',
			required: isGR7_1 && isRenovationSectionB(answers?.typesOfWorkRenovation),
			ref: useRef<HTMLTextAreaElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},

		// Matériau(x) existant(s)
		existingMaterialsRenovationExterior: {
			name: 'existingMaterialsRenovationExterior',
			label: pageAssets?.request_form_commercialPermit_existingMaterials,
			labelKey: 'request_form_commercialPermit_existingMaterials',
			value: answers?.existingMaterialsRenovationExterior?.toString() || '',
			required: false,
			ref: useRef<HTMLTextAreaElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},

		// Matériau(x) projeté(s)
		projectedMaterialsRenovationExterior: {
			name: 'projectedMaterialsRenovationExterior',
			label: pageAssets?.request_form_commercialPermit_projectedMaterials,
			labelKey: 'request_form_commercialPermit_projectedMaterials',
			value: answers?.projectedMaterialsRenovationExterior?.toString() || '',
			required: false,
			ref: useRef<HTMLTextAreaElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},

		// ***************************************
		// Section C -Informations supplémentaires
		// ***************************************

		// Prévoyez-vous l'ajout ou la modification d'une terrasse?
		isTerraceAdditionOrModificationRenovation: {
			name: 'isTerraceAdditionOrModificationRenovation',
			label:
				pageAssets?.request_form_commercialPermit_isTerraceAdditionOrModification,
			labelKey: 'request_form_commercialPermit_isTerraceAdditionOrModification',
			value: answers?.isTerraceAdditionOrModificationRenovation || false,
			required: false,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},

		// Prévoyez-vous des modifications à l'aire de stationnement?
		isParkingAreaModificationRenovation: {
			name: 'isParkingAreaModificationRenovation',
			label:
				pageAssets?.request_form_commercialPermit_isParkingAreaModification,
			labelKey: 'request_form_commercialPermit_isParkingAreaModification',
			value: answers?.isParkingAreaModificationRenovation || false,
			required: false,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},

		// Prévoyez-vous la modification de la superficie de plancher d'un débit de boisson (portion bar)?
		isFloorAreaModificationRenovation: {
			name: 'isFloorAreaModificationRenovation',
			label: pageAssets?.request_form_commercialPermit_isFloorAreaModification,
			labelKey: 'request_form_commercialPermit_isFloorAreaModification',
			value: answers?.isFloorAreaModificationRenovation || false,
			required: false,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		}
	}
}
